import * as React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { TableHead, Table, TableBody } from '@material-ui/core';
import { CustomTableRow, CustomTableCell, RowLabelCell } from '../Common/CustomTableComponents';
import EmptyCols from '../Common/EmptyCols';
import { Indicators } from '../../+store/aToF';
import { d3Indicators, getFilteredIndicators, formatNumberOrDash } from '../Common/utils';
import { getValue } from '../../Common/utils';
import TotalIndicator from '../Common/TotalIndicator';
import classNames from 'classnames';

const isDemo = process.env.REACT_APP_THEME === 'demo';

const styles = (theme) => ({
  root: {
    // background: 'linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%), rgba(249, 128, 29, 0.6);',
    background: isDemo
      ? 'linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%), rgba(249, 128, 29, 0.25);'
      : 'linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%), #F9801D;',
  },
  body: {
    background: isDemo
      ? 'linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%), rgba(249, 128, 29, 0.25);'
      : 'linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%), #F9801D;',
  },
  footer: {
    // background: '#C4C4C4;',
  },
  boldCell: {
    fontWeight: 'bold',
    fontSize: '16px',
  },
  circle: {
    margin: '10px',
    display: 'inline-block',
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    border: isDemo ? '1.5px solid #fff' : '2px solid white',
    background: 'white',
  },
  circle_0: {
    border: isDemo ? '1.5px solid #F37272' : '2px solid red',
    ...(isDemo ? { background: '#fff' } : {}),
  },
  circle_1: {
    backgroundImage: isDemo
      ? undefined
      : 'linear-gradient(00deg,white 50%, transparent 50%),linear-gradient(90deg, red 50%, transparent 50%)',
    ...(isDemo ? { background: 'conic-gradient(#F37272 0% 25%, #ECEFF0 25% 100%)' } : {}),
  },
  circle_2: {
    backgroundImage: isDemo
      ? undefined
      : 'linear-gradient(-90deg, transparent  50%, transparent 50%),linear-gradient(90deg, yellow 50%, transparent 50%);',
    ...(isDemo ? { background: 'conic-gradient(#E7C53C 0% 50%, #ECEFF0 50% 100%)' } : {}),
  },
  circle_3: {
    backgroundImage: isDemo
      ? undefined
      : 'linear-gradient(180deg, transparent 50%, yellow 50%), linear-gradient(90deg, yellow 50%, transparent 50%);',
    ...(isDemo ? { background: 'conic-gradient(#E7C53C 0% 75%, #ECEFF0 75% 100%)' } : {}),
  },
  circle_4: {
    background: isDemo ? '#4CA66B' : 'green',
    // white digit only on the solid green circle; lighter circles keep the default dark digit
    ...(isDemo ? { color: '#fff' } : {}),
  },
  circle_Number: isDemo
    ? {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }
    : {
        position: 'relative',
        top: '50%',
        transform: 'translateY(-50%)',
      },
  circle_disabled_0: {
    border: isDemo ? '1.5px solid #E2E2E2' : '2px dotted #333',
  },
  circle_disabled_1: {
    border: isDemo ? '1.5px solid #E2E2E2' : '2px dotted #333',
    backgroundImage: isDemo
      ? undefined
      : 'linear-gradient(00deg,white 50%, transparent 50%),linear-gradient(90deg, #D9D9D9 50%, transparent 50%)',
    ...(isDemo ? { background: 'conic-gradient(#E2E2E2 0% 25%, #ECEFF0 25% 100%)' } : {}),
  },
  circle_disabled_2: {
    border: isDemo ? '1.5px solid #E2E2E2' : '2px dotted #333',
    backgroundImage: isDemo
      ? undefined
      : 'linear-gradient(-90deg, transparent  50%, transparent 50%),linear-gradient(90deg, #D9D9D9 50%, transparent 50%);',
    ...(isDemo ? { background: 'conic-gradient(#E2E2E2 0% 50%, #ECEFF0 50% 100%)' } : {}),
  },
  circle_disabled_3: {
    border: isDemo ? '1.5px solid #E2E2E2' : '2px dotted #333',
    backgroundImage: isDemo
      ? undefined
      : 'linear-gradient(180deg, transparent 50%, #D9D9D9 50%), linear-gradient(90deg, #D9D9D9 50%, transparent 50%);',
    ...(isDemo ? { background: 'conic-gradient(#E2E2E2 0% 75%, #ECEFF0 75% 100%)' } : {}),
  },
  circle_disabled_4: {
    border: isDemo ? '1.5px solid #E2E2E2' : '2px dotted #333',
    background: isDemo ? '#E2E2E2' : '#D9D9D9',
  },
  circle_NumberDisabled: {
    color: isDemo ? '#777' : '#555',
    fontWeight: 'bold',
  },
});

const totalsColumns = {
  pointsEarned: 'Component Points Earned',
  PointsPossible: 'Component Points Possible',
  percentScore: 'Component Percent Score',
};
const totalsColumnsLength = Object.keys(totalsColumns).length;

const scoreColumns = {
  weight: 'Weight',
  score: 'Score',
};
const scoreColumnsLength = Object.keys(scoreColumns).length;

const RowComponent = ({ label, component, content, style }) => (
  <CustomTableRow>
    <RowLabelCell>{label}</RowLabelCell>
    {Object.entries(d3Indicators).map(([key]) => (
      <CustomTableCell key={key} style={style(component, key)}>
        {content(component, key)}
      </CustomTableCell>
    ))}
    <EmptyCols length={totalsColumnsLength + scoreColumnsLength} />
  </CustomTableRow>
);
RowComponent.propTypes = {
  label: PropTypes.string.isRequired,
  component: PropTypes.object.isRequired,
  content: PropTypes.func.isRequired,
  style: PropTypes.func.isRequired,
};

const getTargetValue = (eligibles, metTargets, indicatorKey, metIndex, showAllTargets) => {
  // if (eligibles && eligibles[indicatorKey]) {
  let metKey = `t${metIndex}`;
  if (
    metTargets &&
    metTargets[indicatorKey] &&
    metTargets[indicatorKey].targets &&
    metTargets[indicatorKey].targets[metKey]
  ) {
    var target = metTargets[indicatorKey].targets[metKey];
    var isValidTarget = isValidTargetValue(metTargets[indicatorKey].targets, metIndex);
    if (showAllTargets == true || isValidTarget) {
      if (!isNaN(target)) {
        return target;
      }
    }
  }
  //}
  return '-';
};

const isValidTargetValue = (targets, metIndex) => {
  for (let point = metIndex + 1; point < 5; point++) {
    if (targets[`t${metIndex}`] > targets[`t${point}`]) {
      return false;
    }
  }
  return true;
};

const SchoolQuality = ({ classes, component, schoolTypeConversion, showAllTargets, showAdditionalStudentGroups }) => {
  if (!component) return null;
  let indicatorsToUse = getFilteredIndicators(Indicators, showAdditionalStudentGroups);
  const indicatorsLength = Object.keys(indicatorsToUse).length;
  return (
    <Table className={classes.root}>
      <TableHead>
        <CustomTableRow>
          <CustomTableCell />
          {Object.entries(indicatorsToUse).map(([key, label]) => (
            <CustomTableCell key={key}>{label}</CustomTableCell>
          ))}
          {Object.entries(totalsColumns).map(([key, label]) => (
            <CustomTableCell key={key}>{label}</CustomTableCell>
          ))}
          {Object.entries(scoreColumns).map(([key, label]) => (
            <CustomTableCell key={key}>{label}</CustomTableCell>
          ))}
        </CustomTableRow>
      </TableHead>
      <TableBody>
        <CustomTableRow>
          <RowLabelCell>School Quality</RowLabelCell>
          <EmptyCols length={indicatorsLength + totalsColumnsLength + scoreColumnsLength} />
        </CustomTableRow>

        <CustomTableRow>
          <RowLabelCell>{`Included`}</RowLabelCell>
          {Object.entries(indicatorsToUse).map(([key]) => (
            <CustomTableCell key={key}>
              {component.eligible &&
              component.eligible[key] &&
              component.componentIndicators &&
              component.componentIndicators.indexOf(key) >= 0
                ? 'Y'
                : '-'}
            </CustomTableCell>
          ))}
          <EmptyCols length={totalsColumnsLength + scoreColumnsLength} />
        </CustomTableRow>
        <RowComponent
          label="% Approaches"
          component={component}
          content={(com, key) => formatNumberOrDash(getValue(com, 'approaches', key), '%')}
          style={() => ({})}
        />
        <RowComponent
          label="% Meets"
          component={component}
          content={(com, key) => formatNumberOrDash(getValue(com, 'meets', key), '%')}
          style={() => ({})}
        />
        <RowComponent
          label="% Masters"
          component={component}
          content={(com, key) => formatNumberOrDash(getValue(com, 'masters', key), '%')}
          style={() => ({})}
        />
        <RowComponent
          label="Total Tests"
          component={component}
          content={(com, key) => formatNumberOrDash(getValue(com, 'denominator', key))}
          style={() => ({})}
        />

        <RowComponent
          label="Score"
          component={component}
          content={(com, key) => formatNumberOrDash(getValue(com, 'rate', key), '%')}
          style={() => ({})}
        />

        <CustomTableRow>
          <RowLabelCell>{`4 Points`}</RowLabelCell>
          {Object.entries(indicatorsToUse).map(([key]) => (
            <CustomTableCell key={key}>
              {getTargetValue(component.eligible, component.metTarget, key, 4, showAllTargets)}
            </CustomTableCell>
          ))}
          <EmptyCols length={totalsColumnsLength + scoreColumnsLength} />
        </CustomTableRow>
        <CustomTableRow>
          <RowLabelCell>{`3 Points`}</RowLabelCell>
          {Object.entries(indicatorsToUse).map(([key]) => (
            <CustomTableCell key={key}>
              {getTargetValue(component.eligible, component.metTarget, key, 3, showAllTargets)}
            </CustomTableCell>
          ))}
          <EmptyCols length={totalsColumnsLength + scoreColumnsLength} />
        </CustomTableRow>

        <CustomTableRow>
          <RowLabelCell>{`2 Points`}</RowLabelCell>
          {Object.entries(indicatorsToUse).map(([key]) => (
            <CustomTableCell key={key}>
              {getTargetValue(component.eligible, component.metTarget, key, 2, showAllTargets)}
            </CustomTableCell>
          ))}
          <EmptyCols length={totalsColumnsLength + scoreColumnsLength} />
        </CustomTableRow>

        <CustomTableRow>
          <RowLabelCell>{`1 Point`}</RowLabelCell>
          {Object.entries(indicatorsToUse).map(([key]) => (
            <CustomTableCell key={key}>
              {getTargetValue(component.eligible, component.metTarget, key, 1, showAllTargets)}
            </CustomTableCell>
          ))}
          <EmptyCols length={totalsColumnsLength + scoreColumnsLength} />
        </CustomTableRow>

        <CustomTableRow>
          <RowLabelCell className={classes.boldCell}>{`Earned`}</RowLabelCell>
          {Object.entries(indicatorsToUse).map(([key]) => {
            const mt = component.metTarget && component.metTarget[key] ? component.metTarget[key] : { point: '-' };
            const point = mt.point;
            const isEligible = component.eligible && component.eligible[key];
            const activeClass = point !== '-' ? classes[`circle_${point}`] : null;
            const disabledClass = !isEligible && point !== '-' ? classes[`circle_disabled_${point}`] : null;
            return (
              <CustomTableCell className={classes.boldCell} key={key}>
                <div className={classNames(classes.circle, activeClass, disabledClass)}>
                  <div className={classNames(classes.circle_Number, !isEligible && classes.circle_NumberDisabled)}>
                    {point}
                  </div>
                </div>
              </CustomTableCell>
            );
          })}

          <EmptyCols length={totalsColumnsLength + scoreColumnsLength} />
        </CustomTableRow>
      </TableBody>
      <TotalIndicator
        footerClass={classes.footer}
        title="School Quality Total"
        indicatorsLength={indicatorsLength}
        totalIndicators={component.totalIndicators}
      />
    </Table>
  );
};
SchoolQuality.propTypes = {
  component: PropTypes.object,
  classes: PropTypes.any,
  showAllTargets: PropTypes.bool,
};

export default withStyles(styles)(SchoolQuality);
